import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSyncAlt } from 'react-icons/fa';
import RTButton from '../common/RTButton';
import RTPageLayout from '../common/RTPageLayout';
import UpdateAllocationStatusModal from '../bedallocation/UpdateAllocationStatusModal';
import AssignComplaintModal from '../complaints/AssignComplaintModal';
import { apiGet } from '../../api/apiClient';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import './AdminDashboard.css';
import tenantStorage from '../../utils/tenantStorage';

const AdminDashboard = ({ data, onDataRefresh }) => {
  const navigate = useNavigate();
  const [adminName, setAdminName] = useState('');
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    mode: '',
    allocation: null,
  });
  const [assignModal, setAssignModal] = useState({
    isOpen: false,
    complaint: null,
  });

  useEffect(() => {
    // Get admin name from localStorage or use default
    const name =
      tenantStorage.getItem('staffName') ||
      tenantStorage.getItem('userName') ||
      'Admin';
    setAdminName(name);
  }, []);

  // Get pending actions from dashboard data
  const pendingActions = data?.pending_actions || [];

  // Handle start allocation
  const handleStartAllocation = (allocation) => {
    setStatusModal({
      isOpen: true,
      mode: 'start',
      allocation: allocation,
    });
  };

  // Handle end allocation
  const handleEndAllocation = (allocation) => {
    setStatusModal({
      isOpen: true,
      mode: 'end',
      allocation: allocation,
    });
  };

  // Handle modal success
  const handleModalSuccess = () => {
    // Refresh dashboard data (will show full page spinner)
    if (onDataRefresh) {
      onDataRefresh();
    }
    // Close modal
    setStatusModal({ isOpen: false, mode: '', allocation: null });
  };

  // Close modal
  const closeModal = () => {
    setStatusModal({ isOpen: false, mode: '', allocation: null });
  };

  // Handle assign complaint
  const handleAssignComplaint = (complaint) => {
    setAssignModal({
      isOpen: true,
      complaint: complaint,
    });
  };

  // Handle assign modal success
  const handleAssignModalSuccess = () => {
    // Refresh dashboard data
    if (onDataRefresh) {
      onDataRefresh();
    }
    // Close modal
    setAssignModal({ isOpen: false, complaint: null });
  };

  // Close assign modal
  const closeAssignModal = () => {
    setAssignModal({ isOpen: false, complaint: null });
  };

  // Navigate to View All Open Complaints
  const handleViewAllComplaints = () => {
    window.location.href = '/complaints/view';
  };

  if (!data) {
    return <div>No dashboard data available</div>;
  }

  return (
    <>
      <RTPageLayout
        menu={false}
        name={adminName}
        title="Dashboard"
        mainSection
        actions={
          <RTButton
            variant="primary"
            onClick={() => {
              if (onDataRefresh) {
                onDataRefresh();
              } else {
                window.location.reload();
              }
            }}
            title="Refresh Dashboard"
            className="refresh-button"
          >
            <span className="refresh-icon">
              <FaSyncAlt />
            </span>
            <span className="refresh-text">Refresh</span>
          </RTButton>
        }
      >
        <div className="admin-dashboard">
          {/* Dashboard Metrics */}
          <div className="dashboard-metrics">
            <div className="metrics-card">
              <div className="metrics-row">
                {/* Active Allocations Widget */}
                <div className="metric-item">
                  <div className="metric-pill total-assigned">
                    <div className="metric-number">
                      {data.total_active_allocations || 0}
                    </div>
                    <div className="dashboard-metric-label">
                      Active Allocations
                    </div>
                  </div>
                </div>

                {/* Vacant Beds Widget */}
                <div className="metric-item">
                  <div className="metric-pill resolved-today">
                    <div className="metric-number">
                      {data.total_vacant_beds || 0}
                    </div>
                    <div className="dashboard-metric-label">Vacant Beds</div>
                  </div>
                </div>

                {/* Total Buildings */}
                <div className="metric-item">
                  <div className="metric-pill total-buildings">
                    <div className="metric-number">
                      {data.total_buildings || 0}
                    </div>
                    <div className="dashboard-metric-label">
                      Total Buildings
                    </div>
                  </div>
                </div>

                {/* Open Complaints Widget */}
                <div className="metric-item">
                  <div className="metric-pill open-complaints">
                    <div className="metric-number">
                      {data.total_open_complaints || 0}
                    </div>
                    <div className="dashboard-metric-label">
                      Open Complaints
                    </div>
                  </div>
                </div>

                {/* Invoices Paid/Raised Widget */}
                <div className="metric-item">
                  <div className="metric-pill invoice-summary">
                    <div className="metric-number">
                      {data.total_invoices_paid_this_month || 0}/
                      {data.total_invoices_this_month || 0}
                    </div>
                    <div className="dashboard-metric-label">
                      Invoices Paid This Month
                    </div>
                  </div>
                </div>

                {/* Amount Outstanding */}
                <div className="metric-item">
                  <div className="metric-pill amount-outstanding">
                    <div className="metric-number">
                      ₹
                      {(data.total_amount_outstanding || 0).toLocaleString(
                        'en-IN',
                        { maximumFractionDigits: 0 },
                      )}
                    </div>
                    <div className="dashboard-metric-label">
                      Total Amount Outstanding
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Required Section - Two Column Layout */}
          <div className="actions-section">
            <h3 className="actions-title">Action Required</h3>

            <div className="action-required-container">
              {/* Allocation Actions Column */}
              <div className="action-column">
                <div className="action-column-header">
                  <h4 className="column-title">
                    Allocation Actions ({pendingActions.length})
                  </h4>
                </div>
                <div className="action-content">
                  {pendingActions.length === 0 ? (
                    <div className="no-actions-message">
                      <div className="no-actions-icon">✅</div>
                      <div className="no-actions-text">No Pending Actions</div>
                      <div className="no-actions-subtext">
                        All allocations are up to date!
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Desktop View */}
                      <div className="desktop-table">
                        {pendingActions.map((allocation) => {
                          const canStart = allocation.action_type === 'start';
                          const canEnd = allocation.action_type === 'end';

                          return (
                            <div
                              key={allocation.allocation_id}
                              className="action-item"
                            >
                              <div className="action-info">
                                <div className="action-primary">
                                  <strong>{allocation.pg_name}</strong> -{' '}
                                  {allocation.building_name}
                                </div>
                                <div className="action-secondary">
                                  Room: {allocation.room_name} |
                                  {canStart &&
                                    ` Start Date: ${formatDateToDDMMYYYY(allocation.start_date)}`}
                                  {canEnd &&
                                    ` End Date: ${formatDateToDDMMYYYY(allocation.end_date)}`}
                                </div>
                              </div>
                              <div className="action-buttons">
                                {canStart && (
                                  <RTButton
                                    variant="primary"
                                    permissionKey="bedmanagement.allocate_bed"
                                    onClick={() =>
                                      handleStartAllocation(allocation)
                                    }
                                    className="action-button start-button"
                                  >
                                    Start Allocation
                                  </RTButton>
                                )}
                                {canEnd && (
                                  <RTButton
                                    variant="primary"
                                    permissionKey="bedmanagement.allocate_bed"
                                    onClick={() =>
                                      handleEndAllocation(allocation)
                                    }
                                    className="action-button end-button"
                                  >
                                    End Allocation
                                  </RTButton>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Mobile Card View */}
                      <div className="mobile-cards">
                        {pendingActions.map((allocation) => {
                          const canStart = allocation.action_type === 'start';
                          const canEnd = allocation.action_type === 'end';

                          return (
                            <div
                              key={allocation.allocation_id}
                              className="mobile-card"
                            >
                              <div className="mobile-card-header">
                                <div className="action-id">
                                  #{allocation.allocation_id}
                                </div>
                                <div className="mobile-action-button">
                                  {canStart && (
                                    <RTButton
                                      variant="primary"
                                      permissionKey="bedmanagement.allocate_bed"
                                      onClick={() =>
                                        handleStartAllocation(allocation)
                                      }
                                      className="action-button mobile"
                                    >
                                      Start
                                    </RTButton>
                                  )}
                                  {canEnd && (
                                    <RTButton
                                      variant="primary"
                                      permissionKey="bedmanagement.allocate_bed"
                                      onClick={() =>
                                        handleEndAllocation(allocation)
                                      }
                                      className="action-button mobile"
                                    >
                                      End
                                    </RTButton>
                                  )}
                                </div>
                              </div>
                              <div className="mobile-card-body">
                                <div className="mobile-info-row">
                                  <span className="mobile-info-item">
                                    {allocation.pg_name}
                                  </span>
                                  <span className="mobile-info-separator">
                                    •
                                  </span>
                                  <span className="mobile-info-item">
                                    {allocation.building_name}
                                  </span>
                                </div>
                                <div className="mobile-description-row">
                                  <span className="mobile-description">
                                    Room: {allocation.room_name} |
                                    {canStart &&
                                      ` Start: ${formatDateToDDMMYYYY(allocation.start_date)}`}
                                    {canEnd &&
                                      ` End: ${formatDateToDDMMYYYY(allocation.end_date)}`}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* UnAssigned Complaints Column */}
              <div className="action-column">
                <div className="action-column-header">
                  <h4 className="column-title">UnAssigned Complaints</h4>
                  <button
                    className="view-all-link"
                    onClick={handleViewAllComplaints}
                  >
                    View All →
                  </button>
                </div>
                <div className="action-content">
                  {!data.unassigned_complaints ||
                  data.unassigned_complaints.length === 0 ? (
                    <div className="no-complaints">
                      <div className="no-complaints-icon">✅</div>
                      <div className="no-complaints-text">
                        No Unassigned Complaints
                      </div>
                      <div className="no-complaints-subtext">
                        All complaints are assigned!
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Desktop View */}
                      <div className="desktop-table">
                        {data.unassigned_complaints.map((complaint) => (
                          <div
                            key={complaint.complaint_id}
                            className="action-item"
                            onClick={() =>
                              navigate(
                                `/complaints/view/${complaint.complaint_id}`,
                              )
                            }
                          >
                            <div className="action-info">
                              <div className="action-primary">
                                <strong>#{complaint.complaint_id}</strong> -{' '}
                                {complaint.pg_name}
                              </div>
                              <div className="action-secondary">
                                {complaint.building_name} •{' '}
                                {complaint.complaint_type}
                              </div>
                              <div className="action-description">
                                {complaint.description}
                              </div>
                            </div>
                            <div className="action-buttons">
                              <RTButton
                                variant="primary"
                                permissionKey="complaints.add"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssignComplaint(complaint);
                                }}
                                className="action-button assign-button"
                              >
                                Assign
                              </RTButton>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Mobile Card View */}
                      <div className="mobile-cards">
                        {data.unassigned_complaints.map((complaint) => (
                          <div
                            key={complaint.complaint_id}
                            className="mobile-card"
                            onClick={() =>
                              navigate(
                                `/complaints/view/${complaint.complaint_id}`,
                              )
                            }
                          >
                            <div className="mobile-card-header">
                              <div className="action-id">
                                #{complaint.complaint_id}
                              </div>
                              <div className="mobile-action-button">
                                <RTButton
                                  variant="primary"
                                  permissionKey="complaints.add"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAssignComplaint(complaint);
                                  }}
                                  className="action-button mobile"
                                >
                                  Assign
                                </RTButton>
                              </div>
                            </div>
                            <div className="mobile-card-body">
                              <div className="mobile-info-row">
                                <span className="mobile-info-item">
                                  {complaint.pg_name}
                                </span>
                                <span className="mobile-info-separator">•</span>
                                <span className="mobile-info-item">
                                  {complaint.complaint_type}
                                </span>
                                <span className="mobile-info-separator">•</span>
                                <span className="mobile-info-item">
                                  {complaint.building_name}
                                </span>
                              </div>
                              <div className="mobile-description-row">
                                <span className="mobile-description">
                                  {(complaint.description?.length > 30
                                    ? `${complaint.description.substring(0, 30)}...`
                                    : complaint.description) || 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </RTPageLayout>

      {/* Status Update Modal */}
      <UpdateAllocationStatusModal
        isOpen={statusModal.isOpen}
        mode={statusModal.mode}
        allocation={statusModal.allocation}
        onSuccess={handleModalSuccess}
        onCancel={closeModal}
      />

      {/* Assign Complaint Modal */}
      <AssignComplaintModal
        isOpen={assignModal.isOpen}
        complaint={assignModal.complaint}
        onSuccess={handleAssignModalSuccess}
        onClose={closeAssignModal}
      />
    </>
  );
};

export default AdminDashboard;
